import React, { useEffect, useRef } from 'react';

export const TitleScreenBackground: React.FC = React.memo(() => {
  const emberCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const emberCanvas = emberCanvasRef.current;
    if (!emberCanvas || !emberCanvas.parentElement) return;

    const emberCtx = emberCanvas.getContext('2d');
    if (!emberCtx) return;

    let animationFrameId: number;
    let width = emberCanvas.parentElement.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 1200);
    let height = emberCanvas.parentElement.clientHeight || (typeof window !== 'undefined' ? window.innerHeight : 800);

    if (emberCanvas.width !== width) emberCanvas.width = width;
    if (emberCanvas.height !== height) emberCanvas.height = height;

    let rafId: number | null = null;
    let lastW = 0;
    let lastH = 0;

    const updateDimensions = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        if (!emberCanvas || !emberCanvas.parentElement) return;
        const parentW = emberCanvas.parentElement.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 1200);
        const parentH = emberCanvas.parentElement.clientHeight || (typeof window !== 'undefined' ? window.innerHeight : 800);
        if (parentW <= 0 || parentH <= 0) return;

        if (Math.abs(parentW - lastW) < 4 && Math.abs(parentH - lastH) < 4) return;
        lastW = parentW;
        lastH = parentH;

        emberCanvas.width = parentW;
        emberCanvas.height = parentH;
        width = parentW;
        height = parentH;
      });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions, { passive: true });

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateDimensions();
      });
      resizeObserver.observe(emberCanvas.parentElement);
    }

    // Adjust particle count for mobile screens (under 768px) to reduce CPU load significantly
    const isMobileDevice = width < 768 || (typeof window !== 'undefined' && window.innerWidth < 768);
    const particleCount = isMobileDevice ? 18 : 45;
    const particles = Array.from({ length: particleCount }).map(() => ({
      x: Math.random() * width,
      y: Math.random() * height,
      radius: Math.random() * 2.0 + 0.8,
      vy: -(Math.random() * 0.55 + 0.2),
      vx: (Math.random() - 0.5) * 0.3,
      alpha: Math.random() * 0.7 + 0.25,
      decay: Math.random() * 0.004 + 0.002,
      color:
        Math.random() > 0.6
          ? '52, 211, 153' // emerald
          : Math.random() > 0.3
          ? '251, 191, 36' // gold
          : '56, 189, 248', // cyan
    }));

    let lastFrameTime = performance.now();
    const frameInterval = isMobileDevice ? 1000 / 30 : 1000 / 60; // 30fps cap on mobile saves battery and eliminates lag

    // Render loop
    const render = (now: number) => {
      animationFrameId = requestAnimationFrame(render);

      const elapsed = now - lastFrameTime;
      if (elapsed < frameInterval) return;
      lastFrameTime = now - (elapsed % frameInterval);

      emberCtx.clearRect(0, 0, width, height);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.y += p.vy;
        p.x += p.vx + Math.sin(p.y * 0.035) * 0.35;
        p.alpha -= p.decay;

        if (p.alpha <= 0 || p.y < 0) {
          p.x = Math.random() * width;
          p.y = height + Math.random() * 20;
          p.alpha = Math.random() * 0.75 + 0.25;
          p.vy = -(Math.random() * 0.55 + 0.2);
          p.radius = Math.random() * 2.0 + 0.8;
        }

        // Fast canvas drawing without heavy createRadialGradient on every particle every frame
        emberCtx.fillStyle = `rgba(${p.color}, ${p.alpha})`;
        emberCtx.beginPath();
        emberCtx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        emberCtx.fill();
      }
    };

    animationFrameId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', updateDimensions);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  return (
    <div className="w-full h-full relative overflow-hidden bg-[#050813] select-none flex items-center justify-center pointer-events-none">
      {/* 1. Deep Atmospheric Cosmic Night Sky Gradient (Spanning full height from top to bottom) */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#04060f] via-[#081226] via-45% to-[#120f26] pointer-events-none z-0" />

      {/* 2. Soft Ambient Radial Glow behind the central title area */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[1]">
        <div className="w-[85%] max-w-xl h-64 sm:h-96 bg-gradient-to-r from-cyan-500/10 via-emerald-500/15 to-teal-500/10 rounded-full blur-3xl animate-pulse" />
      </div>

      {/* 3. Rising Magical Mana Embers (Full width & height canvas) */}
      <canvas
        ref={emberCanvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none z-[2]"
      />
    </div>
  );
});

export const TitleScreenArtwork: React.FC = React.memo(() => {
  return (
    <div className="w-full h-full relative overflow-hidden bg-transparent select-none flex items-center justify-center">
      {/* GAME TITLE - Centered on upper screen in large format */}
      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4 sm:px-6 md:px-8 py-4 max-w-2xl mx-auto">
        {/* Sacred Tifinagh Script Banner */}
        <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-slate-950/95 via-emerald-950/95 to-slate-950/95 border border-emerald-400/60 px-3 sm:px-4 md:px-5 py-1 sm:py-1.5 rounded-full shadow-[0_0_15px_rgba(16,185,129,0.45)] sm:backdrop-blur-md mb-2.5 sm:mb-3.5 max-w-[95%]">
          <span className="text-amber-300 text-[10px] sm:text-xs md:text-sm font-bold shrink-0">◈</span>
          <span className="text-emerald-300 text-[10px] sm:text-xs md:text-sm tracking-[0.15em] sm:tracking-[0.22em] font-black drop-shadow-[0_0_8px_rgba(52,211,153,0.85)] font-cinzel truncate">
            ⵜⴰⵎⴰⵛⴰⵀⵓⵜ ⵏ ⵡⴰⵡⴰⵍⴻⵏ ⵉⵜⵜⵓⵜⵓⵏ
          </span>
          <span className="text-amber-300 text-[10px] sm:text-xs md:text-sm font-bold shrink-0">◈</span>
        </div>

        {/* Masterpiece Title Layout */}
        <div className="flex flex-col items-center relative select-none w-full max-w-full">
          {/* First Line: LA LÉGENDE */}
          <h1 className="relative font-cinzel-dec text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-[0.14em] sm:tracking-[0.18em] uppercase leading-none">
            <span className="title-cyan-text block transform hover:scale-[1.02] transition-transform">
              LA LÉGENDE
            </span>
          </h1>

          {/* Ornamental Divider with "✦ DES ✦" */}
          <div className="flex items-center justify-center gap-2 sm:gap-3 md:gap-4 my-1.5 sm:my-2 md:my-2.5 w-full max-w-xs sm:max-w-sm md:max-w-md">
            <div className="h-[1.5px] flex-1 bg-gradient-to-r from-transparent via-amber-400 to-amber-300" />
            <span className="font-cinzel text-xs sm:text-sm md:text-base text-amber-200 font-extrabold tracking-[0.25em] uppercase drop-shadow-[0_1px_4px_rgba(0,0,0,0.95)]">
              ✦ DES ✦
            </span>
            <div className="h-[1.5px] flex-1 bg-gradient-to-l from-transparent via-amber-400 to-amber-300" />
          </div>

          {/* Second Line: MOTS OUBLIÉS */}
          <h2 className="relative font-cinzel-dec text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-[0.12em] sm:tracking-[0.16em] uppercase leading-tight">
            <span className="title-emerald-text block transform hover:scale-[1.02] transition-transform">
              MOTS OUBLIÉS
            </span>
          </h2>

          {/* Subtitle Motto */}
          <p className="mt-2 sm:mt-2.5 md:mt-3 text-xs sm:text-sm md:text-base font-cinzel text-cyan-200/95 font-bold uppercase tracking-[0.18em] sm:tracking-[0.24em] drop-shadow-[0_2px_6px_rgba(0,0,0,0.95)]">
            Aventure Mythique en Terre de Kabylie
          </p>

          {/* Sole Creator Signature */}
          <div className="mt-2 sm:mt-2.5 md:mt-3 flex items-center justify-center gap-2 sm:gap-3">
            <div className="h-[1px] w-6 sm:w-10 md:w-16 bg-gradient-to-r from-transparent via-amber-400/80 to-amber-300/40" />
            <p className="text-[10px] sm:text-xs md:text-sm font-cinzel text-amber-200 font-extrabold tracking-[0.18em] sm:tracking-[0.22em] uppercase drop-shadow-[0_1px_4px_rgba(0,0,0,0.95)] whitespace-nowrap">
              Un jeu créé par Yoann Baron
            </p>
            <div className="h-[1px] w-6 sm:w-10 md:w-16 bg-gradient-to-l from-transparent via-amber-400/80 to-amber-300/40" />
          </div>
        </div>
      </div>
    </div>
  );
});

